import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import TooltipButton from "../../../common/tooltipButton";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
} from "../../../../store/slices/learningPathwaySlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";

const LearningPathwayList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
  totalCount,
  isFromAssigment = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ ...query, namePattern: value, pageNo: 1 })
  );
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [createdByNamePattern, setCreatedByNamePattern] = useListSearch(
    query?.createdBy,
    (value) => setQuery({ ...query, createdBy: value, pageNo: 1 })
  );

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.CreateScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
      sortColumn: query.sortColumn || "name",
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddLearningObject = () => {
    navigate(RouteConstants.APP_ROUTE_CONTENTS_LEARNING_PATHS_ADD);
  };

  const onClone = (id) => {
    navigate(
      `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_PATHS_ADD}?clone=${id}`
    );
  };

  const otherActions = [
    {
      label: "clone",
      icon: "pi pi-clone",
      isAllowed: canAdd,
      action: (data) => onClone(data.id),
    },
  ];

  return (
    <div className="pb-8 md: pb-0">
      {(!isFromAssigment && (
        <CustomBreadCrumbs labels={labels} navigate={navigate} />
      )) ||
        null}
      <div
        className="edexlms-page-content"
        style={isFromAssigment ? { padding: 0 } : {}}
      >
        {(!isFromAssigment && (
          <div className="edexlms-page-header">
            <div className="edexlms-page-title">
              {intl.formatMessage({ id: "learning_programs" })}
            </div>
            <div className="edexlms-page-actions">
              <TooltipButton
                icon="pi pi-plus"
                onClick={handleAddLearningObject}
                size="small"
                label={intl.formatMessage({ id: "add_learning_program" })}
                type="button"
                disabled={!canAdd}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canAdd ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          </div>
        )) ||
          null}
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_learning_programs",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_created_by",
              })}
              input={{
                name: "search",
                value: createdByNamePattern,
                onChange: (e) => setCreatedByNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            totalRecords={totalCount}
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            handleRowClick={(data) => {
              if (!isFromAssigment) {
                navigate(
                  `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_PATHS_EDIT.replace(
                    ":id",
                    data.data.id
                  )}`
                );
              } else {
                navigate(
                  `${RouteConstants.APP_ROUTE_ASSIGNMENTS_LEARNING_PATHS.replace(
                    ":id",
                    data.data.id
                  )}`
                );
              }
            }}
            selectionMode=""
            columns={[
              {
                field: "name",
                header: isFromAssigment ? "learning_program_name" : "name",
                sortable: true,
                body: ListCellRenders.NameAndLogo("name", "bannerImage"),
              },
              {
                field: "type",
                header: "type",
                sortable: false,
                body: (data) => {
                  return intl.formatMessage({
                    id: data?.typeName?.toLowerCase() || "na",
                  });
                },
              },
              // {
              //   field: "tags",
              //   header: "tags",
              //   sortable: false,
              //   body: ListCellRenders.Tags,
              // },
              {
                field: "isLinear",
                header: "is_linear",
                sortable: false,
                body: ListCellRenders.BooleanText("isLinear"),
              },
              {
                field: "rewardId",
                header: "reward",
                sortable: false,
                body: (data) => {
                  return data?.reward?.name || "-";
                },
              },
              {
                field: "pointsOnCompletion",
                header: "point_on_completion",
                sortable: false,
                body: (data) => {
                  return data?.pointsOnCompletion || 0;
                },
              },
              {
                field: "createdByUser",
                header: "created_by",
                sortable: false,
                body: ListCellRenders.CreatedBy,
              },
              {
                field: "createdAt",
                header: "created_at",
                sortable: true,
                body: ListCellRenders.DateTime("createdAt"),
              },
              {
                field: "updatedAt",
                header: "updated_at",
                sortable: true,
                body: ListCellRenders.DateTime("updatedAt"),
              },
              // {
              //   field: "isCertified",
              //   header: "isCertified",
              //   sortable: false,
              //   body: ListCellRenders.BooleanText("isCertified"),
              // },
              // Reward and points columns moved after isLinear column
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions(otherActions),
              },
              // {
              //   field: "actions",
              //   headerIcon: "pi-cog",
              //   align: "center",
              //   body: ListCellRenders.OtherActions([
              //     {
              //       label: "edit",
              //       icon: "pi-pencil",
              //       isAllowed: true,
              //       action: (data) =>
              //         navigate(
              //           `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_PATHS_EDIT.replace(
              //             ":id",
              //             data.id
              //           )}`
              //         ),
              //     },
              //   ]),
              // },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.learningPathways?.data?.list || [],
  action: state?.learningPathways?.action,
  requesting: state?.learningPathways?.requesting,
  error: state?.learningPathways?.error,
  query: state?.learningPathways?.query,
  user: state?.self?.user,
  totalCount: state?.learningPathways?.data?.totalCount,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningPathwayList);
